import { theme } from "antd";
import {
  BookmarkSimpleIcon,
  ChatCircleIcon,
  FileTextIcon,
} from "@phosphor-icons/react";
import type { SidePanelPosition } from "../types";
import {
  PreviewFrame,
  PreviewStage,
  SideGroup,
  DocumentPane,
  KnowledgeHubPane,
  ChatPane,
  PaneLabel,
} from "./LayoutPreview.styles";

interface LayoutPreviewProps {
  sidePanel: SidePanelPosition;
  /** Whether the AI Chat add-on is enabled — read-only reflection of the real
   *  add-on state, there's no separate "chat panel" setting to toggle here. */
  hasChatAddon: boolean;
  documentLabel: string;
  knowledgeHubLabel: string;
}

/**
 * Miniature of the reader layout — a document pane with the Knowledge Hub /
 * chat side group positioned per `sidePanel`. Reacts live to the layout
 * controls (and the AI Chat add-on state) so the user previews the
 * arrangement before creating.
 */
export const LayoutPreview = ({
  sidePanel,
  hasChatAddon,
  documentLabel,
  knowledgeHubLabel,
}: LayoutPreviewProps) => {
  const { token } = theme.useToken();
  const onLeft = sidePanel === "Left";

  // The sidebar itself can't disappear (no "none" option) — only the chat
  // pane within it is conditional, on the AI Chat add-on.
  const sideGroup = (
    <SideGroup>
      <KnowledgeHubPane $token={token}>
        <BookmarkSimpleIcon size={18} weight="fill" />
        <PaneLabel>{knowledgeHubLabel}</PaneLabel>
      </KnowledgeHubPane>
      {hasChatAddon ? (
        <ChatPane $token={token}>
          <ChatCircleIcon size={18} weight="fill" />
        </ChatPane>
      ) : null}
    </SideGroup>
  );

  return (
    <PreviewFrame $token={token}>
      <PreviewStage>
        {onLeft ? sideGroup : null}
        <DocumentPane $token={token}>
          <FileTextIcon size={20} />
          <PaneLabel>{documentLabel}</PaneLabel>
        </DocumentPane>
        {!onLeft ? sideGroup : null}
      </PreviewStage>
    </PreviewFrame>
  );
};

export default LayoutPreview;
